<template>
  <div>
    <div class="admin-home" v-if="hiddenNav">
      <router-view />
    </div>
    <div class="admin-home admin-home-nav" v-else>
      <div style="position: relative">
        <left-menu
          ref="leftMenuRef"
          :menus="menu"
          :bottomEntries="bottomEntries"
          userType="seller"
        />
        <div @click="toggleCollapsed" class="toggle-menu">
          <svg class="icon" aria-hidden="true">
            <use :xlink:href="'#icon-' + (collapsed ? 'zhankai' : 'shouqi')" />
          </svg>
        </div>
      </div>
      <div class="wrap">
        <topBar />
        <div class="content">
          <div class="page">
            <router-view />
          </div>
          <div v-if="!hideSellerLayoutFooter" class="footer">
            <Footer />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="jsx">
import { useRoute } from 'vue-router'
import Footer from '~/components/Footer.vue'
import topBar from '~/components/topBar.vue'
import leftMenu from '~/components/LeftMenu.vue'
import Icon from '~/components/Icon.vue'
import { useUserInfoStore } from '~/storeData/index'
import { h, onBeforeMount, ref, provide, watch, computed } from 'vue'
import { useSystemStore } from '@/storeData/system'
import { useI18n } from 'vue-i18n'
import { persistSellerLocale, getStoredSellerLocale, DEFAULT_SELLER_LOCALE } from '~/i18n'

const { t, locale } = useI18n()
const systemStore = useSystemStore()
const route = useRoute()
/** CPC 创建/编辑向导页：内容区较高，隐藏底部全局 Footer */
const hideSellerLayoutFooter = computed(() => route.name === 'seller cpc create')
const hiddenNav = ref(false)
const store = useUserInfoStore()
const leftMenuRef = ref(null)
const collapsed = ref(false)
provide('leftMenuRef', leftMenuRef)

function createSellerNavMenu(translate) {
  return [
    {
      key: '/seller/dashboard',
      icon: () =>
        h('svg', { class: 'icon', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-home' }),
        ]),
      routeName: 'dashboard',
      title: '',
      label: translate('seller.menu.dashboard'),
    },
    {
      key: '/seller/brand/index',
      icon: () =>
        h('svg', { class: 'icon', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-pinpai' }),
        ]),
      routeName: 'brand-index',
      title: '',
      label: translate('seller.menu.brands'),
    },
    {
      key: '/seller/product',
      icon: () =>
        h('svg', { class: 'icon', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-shangpin' }),
        ]),
      routeName: 'product',
      title: '',
      label: translate('seller.menu.products'),
    },
    {
    key: '/seller/cpc',
    icon: () =>
      h('svg', { class: 'icon', 'aria-hidden': 'true' }, [
        h('use', { 'xlink:href': '#icon-shangpin' }),
      ]),
    routeName: 'cpc',
    title: '',
    label: 'CPC',
  },
    {
      key: '/seller/reports',
      icon: () =>
        h('svg', { class: 'icon', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-chart' }),
        ]),
      title: '',
      label: <div class="reports-beta">{translate('seller.menu.reports')}</div>,
      children: [
        {
          routeName: 'reports-performance',
          key: '/seller/reports/performance',
          preKey: '/seller/reports',
          label: translate('seller.menu.performance'),
          title: translate('seller.menu.performance'),
        },
        {
          preKey: '/seller/reports',
          routeName: 'reports-product',
          key: '/seller/reports/product',
          label: translate('seller.menu.product'),
          title: translate('seller.menu.product'),
        },
        {
          preKey: '/seller/reports',
          routeName: 'reports-brand',
          key: '/seller/reports/brand',
          label: translate('seller.menu.brand'),
          title: translate('seller.menu.brand'),
        },
        {
          preKey: '/seller/reports',
          routeName: 'reports-publisher',
          key: '/seller/reports/publisher',
          label: translate('seller.menu.publisher'),
          title: translate('seller.menu.publisher'),
        },
      ],
    },
    {
      key: '/seller/associateCentral',
      icon: () =>
        h('svg', { class: 'icon', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-lianmengyingxiao' }),
        ]),
      title: '',
      label: <div class="reports-beta">{translate('seller.menu.associateCentral')}</div>,
      children: [
        {
          preKey: '/seller/associateCentral',
          routeName: 'associateCentral-publishers',
          key: '/seller/associateCentral/publishers',
          label: translate('seller.menu.associatePublishers'),
          title: translate('seller.menu.associatePublishers'),
        },
        {
          preKey: '/seller/associateCentral',
          routeName: 'associateCentral-brands',
          key: '/seller/associateCentral/brands',
          label: translate('seller.menu.associateBrands'),
          title: translate('seller.menu.associateBrands'),
        },
        {
          preKey: '/seller/associateCentral',
          routeName: 'associateCentral-product',
          key: '/seller/associateCentral/product',
          label: translate('seller.menu.associateProducts'),
          title: translate('seller.menu.associateProducts'),
        },
        {
          key: '/seller/creatorConnections/authorization',
          preKey: '/seller/associateCentral',
          routeName: 'associateCentral-authorization',
          label: translate('seller.menu.accAuthorization'),
        },
        {
          key: '/seller/associateCentral/campaigns',
          preKey: '/seller/associateCentral',
          routeName: 'associateCentral-campaigns',
          label: translate('seller.menu.accCampaigns'),
        },
        {
          key: '/seller/associateCentral/reports',
          preKey: '/seller/associateCentral',
          routeName: 'seller-associateCentral-reports',
          label: translate('seller.menu.reports'),
          children: [
            {
              key: '/seller/associateCentral/reports/performance',
              preKey: '/seller/associateCentral/reports',
              routeName: 'associateCentral-reports-performance',
              label: translate('seller.menu.performance'),
            },
            {
              key: '/seller/associateCentral/reports/product',
              preKey: '/seller/associateCentral/reports',
              routeName: 'associateCentral-reports-product',
              label: translate('seller.menu.product'),
            },
            {
              key: '/seller/associateCentral/reports/brand',
              preKey: '/seller/associateCentral/reports',
              routeName: 'associateCentral-reports-brand',
              label: translate('seller.menu.brand'),
            },
            {
              key: '/seller/associateCentral/reports/publisher',
              preKey: '/seller/associateCentral/reports',
              routeName: 'associateCentral-reports-publisher',
              label: translate('seller.menu.publisher'),
            },
          ],
        },
        {
          key: '/seller/associateCentral/partnerships',
          preKey: '/seller/associateCentral',
          routeName: 'associateCentral-partnerships',
          label: translate('seller.menu.partnerships'),
        },
      ],
    },
    {
      routeName: 'Settings-paidPlacement',
      icon: () =>
        h('svg', { class: 'icon', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-a-PaidPlacement' }),
        ]),
      key: '/seller/Settings/paidPlacement',
      label: (
        <div class="reports-beta-tag">
          {translate('seller.menu.paidPlacement')}
          <div class="tag-new">
            <Icon name="Tag-New" class="icon" />
          </div>
        </div>
      ),
      title: translate('seller.menu.paidPlacement'),
      children: [
        {
          preKey: '/seller/Settings/paidPlacement',
          routeName: 'settings-paidCampaigns-campaigns',
          key: '/seller/Settings/paidPlacement/campaigns',
          label: translate('seller.menu.campaigns'),
          title: translate('seller.menu.campaigns'),
        },
        {
          preKey: '/seller/Settings/paidPlacement',
          routeName: 'settings-paidCampaigns-placement',
          key: '/seller/Settings/paidPlacement/placement',
          label: translate('seller.menu.placements'),
          title: translate('seller.menu.placements'),
        },
      ],
    },
    {
      key: 'sub-settings',
      icon: () =>
        h('svg', { class: 'icon', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-a-shezhi2' }),
        ]),
      label: translate('seller.menu.settings'),
      children: [
        {
          key: '/seller/connection',
          preKey: 'sub-settings',
          routeName: 'connection',
          label: translate('seller.menu.connections'),
        },
        {
          key: '/seller/account-info',
          preKey: 'sub-settings',
          routeName: 'account-info',
          label: translate('seller.menu.accountInfo'),
        },
        {
          key: '/seller/commission-rule',
          preKey: 'sub-settings',
          routeName: 'commission-rule',
          label: translate('seller.menu.commissionRule'),
        },
        {
          key: '/seller/whitelist',
          preKey: 'sub-settings',
          routeName: 'whitelist',
          label: translate('seller.menu.whitelist'),
        },
        {
          key: '/seller/users-permissions',
          preKey: 'sub-settings',
          routeName: 'users-permissions',
          label: translate('seller.menu.usersPermissions'),
        },
      ],
    },
    {
      key: '/seller/finance',
      icon: () =>
        h('svg', { class: 'icon', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-yinhangka' }),
        ]),
      title: '',
      label: <div class="reports-beta">{translate('seller.menu.finance')}</div>,
      children: [
        {
          routeName: 'finance-payments',
          key: '/seller/finance/payments',
          preKey: '/seller/finance',
          label: translate('seller.menu.payments'),
          title: translate('seller.menu.payments'),
        },
        {
          routeName: 'finance-ar',
          key: '/seller/finance/ar',
          preKey: '/seller/finance',
          label: translate('seller.menu.ar'),
          title: translate('seller.menu.ar'),
        },
        {
          routeName: 'finance-paymentMethod',
          key: '/seller/finance/paymentMethod',
          preKey: '/seller/finance',
          label: translate('seller.menu.paymentMethod'),
          title: translate('seller.menu.paymentMethod'),
        }
      ],
    },
    {
      key: '/seller/publishers',
      icon: () =>
        h('svg', { class: 'icon', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-Publishers-' }),
        ]),
      label: translate('seller.menu.publishers'),
      routeName: 'publishers',
      title: translate('seller.menu.publishers'),
    },
    {
      key: '/seller/sellerAccount',
      icon: () =>
        h('svg', { class: 'icon', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-CC' }),
        ]),
      label: translate('seller.menu.creatorConnections'),
      children: [
        {
          key: '/seller/sellerAccount/authorization',
          preKey: '/seller/sellerAccount',
          routeName: 'sellerAccount-authorization',
          label: translate('seller.menu.accAuthorization'),
        },
        {
          key: '/seller/sellerAccount/campaigns',
          preKey: '/seller/sellerAccount',
          routeName: 'sellerAccount-campaigns',
          label: translate('seller.menu.accCampaigns'),
        },
      ],
    },
    {
      key: '/seller/creatorConnections',
      icon: () =>
        h('svg', { class: 'icon', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-lianmengyingxiao' }),
        ]),
      label: translate('seller.menu.creatorConnections'),
      routeName: 'creatorConnections',
      title: translate('seller.menu.creatorConnections'),
      children: [
        {
          key: '/seller/creatorConnections/accBonusProgram',
          preKey: '/seller/creatorConnections',
          routeName: 'creatorConnections-accBonusProgram',
          label: translate('seller.menu.accBonusProgram'),
        },
        {
          key: '/seller/creatorConnections/authorization',
          preKey: '/seller/creatorConnections',
          routeName: 'creatorConnections-authorization',
          label: translate('seller.menu.accAuthorization'),
        },
        {
          key: '/seller/creatorConnections/campaigns',
          preKey: '/seller/creatorConnections',
          routeName: 'creatorConnections-campaigns',
          label: translate('seller.menu.accCampaigns'),
        }
      ],
    }
  ]
}

function createBottomEntries(translate) {
  return [
    {
      key: '/seller/referral',
      icon: () =>
        h('svg', { class: 'icon', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-zuanshi' }),
        ]),
      routeName: 'referral',
      title: translate('seller.bottom.referFriends'),
      label: translate('seller.bottom.referFriends'),
      path: '/seller/referral',
    },
    {
      key: 'contact-us',
      icon: () =>
        h('svg', { class: 'icon icon-contact-us', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-youjian' }),
        ]),
      label: translate('seller.bottom.contactUs'),
    },
    {
      key: '/help',
      icon: () =>
        h('svg', { class: 'icon icon-help', 'aria-hidden': 'true' }, [
          h('use', { 'xlink:href': '#icon-wenhao' }),
        ]),
      routeName: 'help',
      label: translate('seller.bottom.needHelp'),
    },
  ]
}

const menu = ref([])
const bottomEntries = computed(() => createBottomEntries(t))

function setUserMenus(menus) {
  const defaultMenu = createSellerNavMenu(t)
  if (menus) {
    menu.value = menus.children.map((item) => {
      for (let i = 0; i < defaultMenu.length; i++) {
        if (item.path === defaultMenu[i].key) {
          if (defaultMenu[i].children) {
            const children = []

            for (let j = 0; j < item.children?.length; j++) {
              for (let k = 0; k < defaultMenu[i].children.length; k++) {
                if (item.children[j].path === defaultMenu[i].children[k].key) {
                  children.push(defaultMenu[i].children[k])
                  break
                }
              }
            }

            defaultMenu[i].children = children
          }

          return defaultMenu[i]
        }
      }
    })
  } else {
    menu.value = defaultMenu
  }
}

watch(locale, () => {
  const accountId = store.userInfo?.id ?? store.userInfo?.email
  persistSellerLocale(locale.value, accountId)
  setUserMenus(store.userInfo?.menus)
})
const toggleCollapsed = () => {
  collapsed.value = !collapsed.value
  leftMenuRef.value.toggleCollapsed()
}
onBeforeMount(() => {
  const { meta } = route
  hiddenNav.value = meta.hiddenNav
  const accountId = store.userInfo?.id ?? store.userInfo?.email
  locale.value = getStoredSellerLocale(accountId) || DEFAULT_SELLER_LOCALE
  setUserMenus(store.userInfo.menus)
  systemStore.setPageType('seller')
  let associateCentral = {}
  if(store.userInfo.menus.children){
    associateCentral = store.userInfo.menus.children.find(item=>item.path === '/seller/associateCentral')
  }
  if(associateCentral){
    systemStore.setIsAcc(true)
  }else{
    systemStore.setIsAcc(false)
  }
  // console.log(store.userInfo.menus,'systemStore',systemStore.isAcc)
})
</script>
<style lang="less">
.artmisads-button.artmisads-default {
  border: 1px solid var(--border-1) !important;
}
</style>
<style lang="less" scoped>
.toggle-menu {
  top: 10px;
  z-index: 999;
  width: 24px;
  right: -24px;
  height: 28px;
  cursor: pointer;
  position: absolute;
  background: #ffffff;
  border: 1px solid var(--border-4);
  box-shadow: 0 1px 0 0 var(--box-shadow-2);
  border-radius: 0 8px 8px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--dark-3);

  .icon {
    font-size: 12px;
    font-weight: 600;
  }

  &:hover {
    color: var(--dark-3);
    background-color: var(--bg-gray-1);
    border-color: var(--border-1);
  }

  &:active {
    color: var(--dark-2);
    background-color: var(--bg-gray-2);
    border-color: var(--bg-gray-7);
  }
}
:deep(.ant-menu-title-content) {
  overflow: visible !important;

  .reports-beta-tag {
    position: relative;

    .tag-new {
      position: absolute;
      top: -10px;
      left: -25px;
      z-index: 10;

      .icon {
        font-size: 24px;
      }
    }
  }
}
.admin-home {
  display: flex;
  width: 100dvw;
  height: 100dvh;
  overflow-x: scroll;
  overflow-y: scroll;
}

.admin-home-nav {
  .wrap {
    flex: 1;
    display: flex;
    min-width: 0;
    flex-direction: column;

    .content {
      flex: 1;
      overflow: auto;

      .page {
        min-height: calc(100% - 72px);
      }
    }
  }
}
</style>
